let activeWorkspaceId: string | null = null;
let installed = false;

export function setWorkspaceRequestContext(workspaceId: string | null) {
  activeWorkspaceId = workspaceId;
}

function isWavegenApiRequest(
  input: RequestInfo | URL,
  apiBase = process.env.REACT_APP_API_BASE || "https://api.wavegen.ai",
): boolean {
  const requestUrl =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  try {
    const target = new URL(requestUrl, window.location.origin);
    const api = new URL(apiBase, window.location.origin);
    const apiPath = api.pathname.replace(/\/$/, "");
    return (
      target.origin === api.origin &&
      (target.pathname === apiPath || target.pathname.startsWith(`${apiPath}/`))
    );
  } catch {
    return false;
  }
}

/**
 * Pure request-contract helper used by the global fetch bridge and its rollout
 * tests. It adds context only to authenticated requests to the exact WaveGen
 * API origin, and never replaces an explicitly selected workspace.
 */
export function workspaceHeadersForRequest(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  workspaceId: string | null,
  apiBase?: string,
): Headers {
  const headers = new Headers(
    input instanceof Request ? input.headers : undefined,
  );
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  if (
    workspaceId &&
    headers.has("authorization") &&
    !headers.has("x-workspace-id") &&
    isWavegenApiRequest(input, apiBase)
  ) {
    headers.set("x-workspace-id", workspaceId);
  }
  return headers;
}

/**
 * The workspace headers the fetch bridge would send, for API requests that
 * can't go through fetch (upload progress needs XMLHttpRequest).
 */
export function workspaceHeadersForXhr(url: string, headers: Record<string, string>): Headers {
  return workspaceHeadersForRequest(url, { headers }, activeWorkspaceId);
}

/** Same signal the fetch bridge raises when the server refuses the workspace. */
export function reportWorkspaceAccessLost() {
  window.dispatchEvent(new CustomEvent("wavegen:workspace-access-lost"));
}

/**
 * Adds the workspace displayed in this browser tab to authenticated API calls.
 * Firebase/Storage and third-party fetches are deliberately left untouched.
 */
export function installWorkspaceFetchContext() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const headers = workspaceHeadersForRequest(input, init, activeWorkspaceId);
    return nativeFetch(input, { ...init, headers }).then((response) => {
      if (response.status === 403 && headers.has("authorization")) {
        reportWorkspaceAccessLost();
      }
      return response;
    });
  }) as typeof window.fetch;
}
